<script setup>
import { ref, computed } from "vue"

const props = defineProps({
  type: {
    type: String,
    default: "primary"
  },
  size: {
    type: String,
    default: "md"
  },
  accesskey: {
    type: String,
    default: null
  },
  enabled: {
    type: Boolean,
    default: true
  },
  grid: { //grid action button
    type: Boolean,
    default: false
  },
  initialStep: { //Delay between first press and next counting step
    type: Number,
    default: 700
  },
  normalStep: { //Delay between every consecutive counting step
    type: Number,
    default: 300
  },
  fastStep: { //Delay between every consecutive counting step when switched to fast mode
    type: Number,
    default: 150
  },
  fastAfter: { //Delay from initial press to shift to fast mode
    type: Number,
    default: 1800
  },
});

const emit = defineEmits(["click"]);

const buttonClass = computed(() => {
  let classList = [ ];
  if (!props.enabled) {
    classList.push("button-disabled");
  }
  else if (props.type) {
    classList.push("button-" + props.type);
  }
  if (props.size) {
    classList.push("button-size-" + props.size);
  }
  if (props.grid) {
    classList.push("button-grid-action");
  }
  let className = classList.join(" ");
  return className;
});

function click() {
  if (props.enabled) {
    emit("click");
  }
}

let initialTimer = null;
let normalTimer = null;
let fastTimer = null;
let pressed = false;

function startStepTimer() {
  //Normal interval steps
  if (props.normalStep > 0) {
    normalTimer = setInterval(function() {
      if (pressed) {
        click();
      }
    }, props.normalStep);
  }

  //Shift to speed up steps
  if (props.fastAfter > 0) {
    initialTimer = setTimeout(() => {
      if (pressed) {
        //click();
        startFastTimer();
      }
    }, props.fastAfter);
  }
}

function startFastTimer() {
  if (props.fastStep > 0) {
    if (normalTimer) {
      clearInterval(normalTimer);
      normalTimer = null;
    }

    fastTimer = setInterval(function() {
      if (pressed) {
        click();
      }
    }, props.fastStep);
  }
}

function start() {
  click();
  pressed = true;
  if (props.initialStep > 0) {
    initialTimer = setTimeout(() => {
      if (pressed) {
        click();
        startStepTimer();
      }
    }, props.initialStep);
  }
  else {
    startStepTimer();
  }
}

function end() {
  pressed = false;
  if (initialTimer) {
    clearTimeout(initialTimer);
    initialTimer = null;
  }
  if (normalTimer) {
    clearInterval(normalTimer);
    normalTimer = null;
  }
  if (fastTimer) {
    clearInterval(fastTimer);
    fastTimer = null;
  }
}
</script>

<template>
  <!-- @click="$emit('click')" may be triggered twice -->
  <button type="button" :accesskey="accesskey" :class="buttonClass" @mousedown="start()" @mouseup="end()"><slot /></button>
</template>

<style scoped>
.button-primary, .button-info, .button-question, .button- {
  @apply text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800;
}

.button-success {
  @apply text-white bg-emerald-700 hover:bg-emerald-800 focus:ring-4 focus:outline-none focus:ring-emerald-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-emerald-600 dark:hover:bg-emerald-700 dark:focus:ring-emerald-800;
}

.button-warning {
  @apply text-yellow-800 bg-yellow-300 hover:bg-yellow-400 focus:ring-4 focus:outline-none focus:ring-yellow-200 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-yellow-600 dark:hover:bg-yellow-700 dark:focus:ring-yellow-800;
}

.button-danger, .button-question-danger {
  @apply text-white bg-red-700 hover:bg-red-800 focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-800;
}

.button-secondary {
  @apply text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600;
}

.button-disabled {
  @apply cursor-default text-gray-300 bg-white outline-none rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500;
}

.button-grid-action {
  border: none;
  background: transparent;
  border-radius: 0;
}

.button-grid-action:hover {
  @apply bg-gray-200;
}

.button-size-sm {
  margin: 0 !important;
  padding: 0 !important;
  font-size: 12px;
  line-height: 28px;
  text-align: center;
  width: 28px;
  height: 28px;
}

.items-group .button-primary,
.items-group .button-secondary,
.items-group .button-disabled {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  /* border-left: 0; */
}
</style>
